import { NormalizedNumber } from '@sparkdotfi/common-universal'

import { CapAutomatorInfo } from '../data/cap-automator/types'
import { MarketOverview } from '../types'
import { getReserveEModeCategoryTokens } from './getReserveEModeCategoryTokens'
import { SimplifiedQueryResult } from '@/domain/common/query'
import { MarketSparkRewards } from '@/features/spark-rewards/types'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketRateChartData } from '@/features/sparklend/market-details/data/market-charts/types'

export interface MakeMarketOverviewParams {
  marketInfo: MarketInfo
  reserve: Reserve
  capAutomatorInfo: CapAutomatorInfo
  sparkRewards: MarketSparkRewards[]
  supplyRateHistoricResult?: SimplifiedQueryResult<MarketRateChartData>
  borrowRateHistoricResult?: SimplifiedQueryResult<MarketRateChartData>
}

export function makeMarketOverview({
  reserve,
  marketInfo,
  capAutomatorInfo,
  sparkRewards,
  supplyRateHistoricResult,
  borrowRateHistoricResult,
}: MakeMarketOverviewParams): MarketOverview {
  const eModeCategoryId = reserve.eModeCategory?.id
  const eModeCategoryTokens = getReserveEModeCategoryTokens(marketInfo, reserve)

  const instantlyAvailableToSupply =
    reserve.supplyCap && NormalizedNumber.max(reserve.supplyCap?.minus(reserve.totalLiquidity), NormalizedNumber.ZERO)

  const capLessThanLiquidity = Boolean(reserve.borrowCap?.lt(reserve.totalLiquidity))
  const borrowLiquidity = NormalizedNumber.max(
    capLessThanLiquidity ? reserve.borrowCap!.minus(reserve.totalDebt) : reserve.availableLiquidity,
    NormalizedNumber.ZERO,
  )

  const supplySparkRewards = sparkRewards.filter((reward) => reward.action === 'supply')
  const borrowSparkRewards = sparkRewards.filter((reward) => reward.action === 'borrow')

  return {
    supply: {
      status: reserve.supplyAvailabilityStatus,
      totalSupplied: reserve.totalLiquidity,
      supplyCap: reserve.supplyCap,
      instantlyAvailableToSupply,
      apy: reserve.supplyAPY,
      capAutomatorInfo: capAutomatorInfo.supplyCap,
      sparkRewards: supplySparkRewards,
      supplyRateHistoricResult: reserve.borrowEligibilityStatus === 'no' ? undefined : supplyRateHistoricResult, // when borrowing is disabled, supply rate is not available
    },
    collateral: {
      status: reserve.collateralEligibilityStatus,
      token: reserve.token,
      maxLtv: reserve.maxLtv,
      liquidationThreshold: reserve.liquidationThreshold,
      liquidationPenalty: reserve.liquidationBonus,
      isolationModeInfo: { debt: reserve.isolationModeTotalDebt, debtCeiling: reserve.debtCeiling },
    },
    borrow: {
      status: reserve.borrowEligibilityStatus,
      totalBorrowed: reserve.totalDebt,
      borrowLiquidity,
      borrowCap: reserve.borrowCap,
      limitedByBorrowCap: capLessThanLiquidity,
      apy: reserve.variableBorrowApy,
      reserveFactor: reserve.reserveFactor,
      capAutomatorInfo: capAutomatorInfo.borrowCap,
      sparkRewards: borrowSparkRewards,
      borrowRateHistoricResult,
    },
    summary: {
      marketSize: reserve.totalLiquidity,
      utilizationRate: reserve.utilizationRate,
      borrowed: reserve.totalDebt,
      available: reserve.availableLiquidity,
    },
    ...(eModeCategoryId === 1 || eModeCategoryId === 2 || eModeCategoryId === 3
      ? {
          eMode: {
            maxLtv: reserve.eModeCategory!.ltv,
            liquidationThreshold: reserve.eModeCategory!.liquidationThreshold,
            liquidationPenalty: reserve.eModeCategory!.liquidationBonus,
            categoryId: eModeCategoryId,
            eModeCategoryTokens,
          },
        }
      : {}),
  }
}
